/**
 * Helpers pra gerar celulas numericas no export .xlsx (Task #74).
 *
 * Por que existe:
 * Antes da #74 o ExportDropdown chamava `format(row)` retornando STRING
 * pre-formatada ("R$ 145.678"). Excel interpretava como texto, impedindo
 * SUM/AVG nas colunas. Veiga 29/04 reportou em screenshot: Mesa abre
 * .xlsx, tenta agregar coluna Valor, nao funciona.
 *
 * Fix:
 * Coluna agora pode retornar `NumericCell` ({ value: number, numFmt: ... })
 * em vez de string. ExportDropdown detecta esse shape e escreve a celula
 * como tipo 'n' (numero) + cell.z = numFmt (formato BR via locale Excel).
 * SUM funciona, AVG funciona, agregacoes de tabela dinamica funcionam.
 *
 * Strings continuam aceitas pra colunas legitimamente texto (Contraparte,
 * BU, Submercado, Contrato BBCE) — backward-compatible.
 *
 * Reuso planejado: #67 (Export Curva), #75+ (futuros exports). Helpers
 * centrais aqui evitam re-inventar formatos pt-BR em N componentes.
 */

/** Marker pra ExportDropdown reconhecer celula numerica com formato. */
export interface NumericCell {
  __cellType: "numeric";
  value: number;
  numFmt: string;
}

/** Marker pra celula de data — Excel-native, agrega em pivot tables. */
export interface DateCell {
  __cellType: "date";
  value: Date;
  numFmt: string;
}

export type FormattedCell = NumericCell | DateCell;

// ---------- Formatos Excel pt-BR ----------

/** R$ X.XXX,XX com vermelho pra negativos. Sem decimais. */
export const FMT_BRL_INT = 'R$ #,##0;[Red]-R$ #,##0';
/** R$ X.XXX,XX com 2 decimais + vermelho negativos. */
export const FMT_BRL = 'R$ #,##0.00;[Red]-R$ #,##0.00';
/** Numero generico 2 decimais, separador milhar pt-BR. */
export const FMT_NUM_2 = '#,##0.00';
/** Numero 4 decimais (volume MWh). */
export const FMT_NUM_4 = '#,##0.0000';
/** Inteiro com separador de milhar. */
export const FMT_INT = '#,##0';
/** Data dd/mm/yyyy. */
export const FMT_DATE = 'dd/mm/yyyy';

// ---------- Type guards ----------

export function isFormattedCell(v: unknown): v is FormattedCell {
  return (
    typeof v === "object" &&
    v !== null &&
    "__cellType" in v &&
    "value" in v &&
    "numFmt" in v
  );
}

// ---------- Builders ----------

/** R$ inteiro (sem decimais). Pra valores grandes onde centavos
 * nao agregam (ex: Net Financeiro 5.439.872). */
export function brlInt(value: number | null | undefined): NumericCell | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return { __cellType: "numeric", value, numFmt: FMT_BRL_INT };
}

/** R$ com 2 decimais. Pra precos unitarios e valores onde precisao importa. */
export function brl(value: number | null | undefined): NumericCell | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return { __cellType: "numeric", value, numFmt: FMT_BRL };
}

/** Numero generico com `decimals` casas (default 2). MWh tipicamente 4. */
export function num(value: number | null | undefined, decimals = 2): NumericCell | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  const numFmt = decimals === 0 ? FMT_INT : `#,##0.${"0".repeat(decimals)}`;
  return { __cellType: "numeric", value, numFmt };
}

/** Inteiro com separador. */
export function int(value: number | null | undefined): NumericCell | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return { __cellType: "numeric", value, numFmt: FMT_INT };
}

/** Data dd/mm/yyyy. Aceita Date ou string ISO YYYY-MM-DD.
 *
 * Task #84 fix: constroi a Date ao MEIO-DIA local (12:00), nao a meia-noite.
 *
 * Por que: SheetJS converte Date JS → serial Excel via `(getTime() - epoch)
 * / 86400000`. Quando construimos `new Date(y, m-1, day)` (meia-noite
 * local), em Brasil (UTC-3) isso vira `00:00 BRT = 03:00 UTC`. Excel
 * renderiza usando timezone local — em maquinas com TZ default UTC
 * (CI, alguns containers, browsers em PCs de TI mal configurados), os
 * 03:00 UTC viram dia anterior. Dezembro/01 vira novembro/30, mes anterior
 * em casos limite.
 *
 * O fix do meio-dia local garante margem de ±12h de TZ shift sem rollover
 * — qualquer offset entre UTC-12 e UTC+11 mantem o dia correto. Cobre
 * todos os fusos do mundo, inclusive offsets de 30/45 minutos (Iran,
 * Newfoundland, Nepal).
 *
 * Tested invariant: `dateCell(...).value.getHours() === 12` em qualquer
 * timezone — protege contra regressao do bug.
 */
export function dateCell(value: Date | string | null | undefined): DateCell | null {
  if (value === null || value === undefined) return null;
  let y: number, m: number, day: number;
  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime())) return null;
    // Extrai componentes locais — re-cria a date ao meio-dia pra
    // normalizar (eventual horario diferente vira 12:00 do mesmo dia).
    y = value.getFullYear();
    m = value.getMonth() + 1;
    day = value.getDate();
  } else {
    const parts = value.split("-").map(Number);
    [y, m, day] = parts;
    if (!y || !m || !day) return null;
  }
  const d = new Date(y, m - 1, day, 12, 0, 0, 0);
  if (!Number.isFinite(d.getTime())) return null;
  return { __cellType: "date", value: d, numFmt: FMT_DATE };
}

// ---------- Formula injection (CWE-1236) — D-148 portado pro Cortex ----------

/** Caracteres que, no INICIO de uma celula, o Excel/LibreOffice interpretam como
 * FORMULA VIVA ao abrir (= + - @) ou quebram a coluna (TAB \t / CR \r / LF \n).
 * Vetor de formula injection quando o texto vem de TERCEIRO (nome/razao/descricao/
 * observacao do cadastro Omie/Zeus — a Indra nao controla). */
const FORMULA_TRIGGER = /^[=+@\t\r\n-]/;

/** Sanitiza UM valor de celula contra formula injection (CWE-1236). SO mexe em
 * STRING: se comeca com gatilho, prefixa apostrofo (') — Excel/LibreOffice tratam
 * como TEXTO, nao formula. Numero/Date/FormattedCell/qualquer nao-string passam
 * INTACTOS (numero negativo -1234.56 NAO e prefixado -> sem regressao de formato).
 *
 * Ponto unico: aplicado no csvEscape E no resolveXlsxCell do ExportDropdown ->
 * cobre TODOS os exports (Fluxo #30, Composicao #38, Varejo ABC, Liquidacoes, DRE,
 * Despesas, RH) de uma vez. Defense-in-depth: nao dependemos de o SheetJS gravar
 * string como type-'s' (pode mudar num bump da lib). */
export function sanitizeCellValue<T>(v: T): T | string {
  if (typeof v !== "string" || v.length === 0) return v;
  return FORMULA_TRIGGER.test(v) ? `'${v}` : v;
}

/** Pra fallback de CSV / debug — converte celula formatada em string com
 * separador BR. Nao usar pra Excel; o ExportDropdown trata FormattedCell
 * direto. */
export function cellToCsvString(cell: FormattedCell | string | number | null): string {
  if (cell === null) return "";
  if (typeof cell === "string") return cell;
  if (typeof cell === "number") return cell.toLocaleString("pt-BR");
  if (cell.__cellType === "date") {
    const d = cell.value;
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  }
  return cell.value.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}
